import type { ReactNode } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { PROFILE_SIDEBAR_WIDTH } from "@/features/candidate/lib/profile-constants";
import { cn } from "@/lib/utils";

export interface ProfileSidebarColumnProps {
  /** Optional className override; merged via `cn()` over the base styling. */
  className?: string;
  /**
   * Forwarded to the inner {@link ScrollArea}. Constrains content to the
   * column width (vertical-scroll-only surfaces) instead of Base UI's default
   * `min-width: fit-content`.
   */
  constrainContentWidth?: boolean;
  children: ReactNode;
}

/**
 * Desktop-only sidebar column rendered alongside the main scroll area on
 * search/profile and jobs/pipeline/profile. Applies the canonical
 * `"hidden md:flex min-h-0 shrink-0 flex-col bg-surface-elevated"`
 * styling and pins the width to {@link PROFILE_SIDEBAR_WIDTH}. The contents
 * are wrapped in a `ScrollArea` so the sidebar scrolls independently of the
 * main column on desktop.
 *
 * For the mobile-only sidebar that renders inline below the main column on
 * narrow viewports, see {@link ProfileMobileSidebar}.
 */
export function ProfileSidebarColumn({
  className,
  constrainContentWidth,
  children,
}: ProfileSidebarColumnProps) {
  return (
    <div
      className={cn(
        "hidden md:flex min-h-0 shrink-0 flex-col border-l border-border bg-surface-elevated",
        className,
      )}
      style={{ width: PROFILE_SIDEBAR_WIDTH }}
    >
      <ScrollArea
        className="min-h-0 flex-1"
        constrainContentWidth={constrainContentWidth}
      >
        {children}
      </ScrollArea>
    </div>
  );
}
